import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { Loader2, ShieldCheck, AlertCircle } from "lucide-react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { API } from "../../lib/api";

const AcceptInvite = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [loading, setLoading] = useState(true);
  const [invitee, setInvitee] = useState(null);
  const [err, setErr] = useState(null);
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/invite/${token}`);
        setInvitee(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "This invite link is invalid or expired.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) return setErr("Password must be at least 8 characters.");
    if (pwd !== confirm) return setErr("Passwords don't match.");
    setSubmitting(true);
    setErr(null);
    try {
      await axios.post(`${API}/auth/accept-invite`, { token, password: pwd }, { withCredentials: true });
      await refresh();
      navigate("/admin", { replace: true });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not activate the account.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A192F]">
        <Loader2 className="text-[#D4AF37] animate-spin" size={32} />
      </div>
    );
  }
  if (err && !invitee) return <ErrorScreen err={err} />;

  return (
    <div className="min-h-screen bg-[#0A192F] flex items-center justify-center px-5 py-10" data-testid="accept-invite-page">
      <div className="bg-white max-w-md w-full p-8 sm:p-10">
        <div className="flex items-center gap-3">
          <ShieldCheck size={22} className="text-[#D4AF37]" />
          <div className="font-display text-[11px] uppercase tracking-[0.3em] text-[#D4AF37]">
            Activate Account
          </div>
        </div>
        <h1 className="mt-4 font-display font-black text-3xl text-[#0A192F] tracking-tighter">Welcome, {invitee.name || "there"}.</h1>
        <p className="mt-3 text-sm text-[#0A192F]/70">
          Set a password to activate <strong>{invitee.email}</strong>. The link expires in 72 hours.
        </p>

        <form onSubmit={submit} className="mt-7 space-y-4">
          <Field label="New Password" type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} testId="invite-password" required />
          <Field label="Confirm Password" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} testId="invite-password-confirm" required />
          {err && (
            <div className="flex items-start gap-2 bg-red-50 border-l-4 border-red-500 p-3 text-sm text-red-800">
              <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
              <span>{err}</span>
            </div>
          )}
          <button
            type="submit"
            disabled={submitting}
            data-testid="invite-submit"
            className="w-full bg-[#0A192F] text-white px-6 py-3.5 font-display text-xs uppercase tracking-[0.3em] font-bold hover:bg-[#D4AF37] hover:text-[#0A192F] transition-colors disabled:opacity-50"
          >
            {submitting ? <Loader2 size={14} className="animate-spin inline" /> : "Activate & Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
};

const ErrorScreen = ({ err }) => (
  <div className="min-h-screen bg-[#0A192F] flex items-center justify-center px-5">
    <div className="bg-white max-w-md w-full p-8 text-center">
      <AlertCircle size={36} className="text-red-500 mx-auto" />
      <h1 className="mt-5 font-display font-black text-2xl text-[#0A192F]">Invite unavailable</h1>
      <p className="mt-3 text-sm text-[#0A192F]/70">{err}</p>
      <Navigate to="/" />
    </div>
  </div>
);

const Field = ({ label, testId, ...props }) => (
  <div>
    <label className="font-display text-[10px] uppercase tracking-[0.3em] text-[#0A192F]/70 font-semibold">
      {label}
    </label>
    <input
      {...props}
      data-testid={testId}
      className="mt-2 w-full border border-gray-300 px-3 py-2.5 focus:border-[#D4AF37] focus:outline-none"
    />
  </div>
);

export default AcceptInvite;
